import React, { useState, useRef, useEffect } from "react";
import { uploadReorder, autoImportStatus, autoImportRun } from "../services/api";
import { Button, LoadingBar } from "./common/ui";

const FileIcon = ({ done }) => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" />
    {done ? <path d="m9 15 2 2 4-4" /> : <path d="M12 18v-6M9 15l3-3 3 3" />}
  </svg>
);

const todayStr = () => new Date().toISOString().split("T")[0];

export default function UploadSection({ onUploaded, showToast }) {
  const [file, setFile] = useState(null);
  const [date, setDate] = useState(todayStr());
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [autoStatus, setAutoStatus] = useState(null); // { found, filename, already_imported, date }
  const [autoImporting, setAutoImporting] = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    autoImportStatus()
      .then(setAutoStatus)
      .catch(() => {});
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) setFile(f);
  };

  const handleSubmit = async () => {
    if (!file || !date) return;
    setLoading(true);
    try {
      const res = await uploadReorder(file, date);
      showToast(`Uploaded: ${res.total_styles} styles, ${res.total_rows} rows`, "success");
      onUploaded(res.session_id);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Upload failed";
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAutoImport = async () => {
    setAutoImporting(true);
    try {
      const res = await autoImportRun();
      if (res.already_imported) {
        showToast(`Already imported: ${res.filename}`, "success");
      } else {
        showToast(`Auto-imported: ${res.total_styles} styles, ${res.total_rows} rows`, "success");
        onUploaded(res.session_id);
      }
      setAutoStatus(s => ({ ...s, already_imported: true }));
    } catch (err) {
      showToast(err.response?.data?.detail || "Auto-import failed", "error");
    } finally {
      setAutoImporting(false);
    }
  };

  return (
    <div className="upload-card">
      <h2 title="Upload the Excel reorder file exported from Power BI. The app reads the Master Reorder sheet and maps requested qty to the latest PO report." style={{ marginBottom: 16 }}>Upload reorder report</h2>

      {/* Auto-import banner */}
      {autoStatus?.found && !autoStatus.already_imported && (
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          background: "var(--success-bg)", color: "var(--text)", borderRadius: "var(--radius-sm)",
          border: "1px solid var(--success-bg)", borderLeft: "3px solid var(--success)",
          padding: "10px 12px", marginBottom: 16, flexWrap: "wrap",
        }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 13, overflowWrap: "anywhere" }}>
            <strong>{autoStatus.filename}</strong> <span style={{ color: "var(--text-muted)" }}>found in sourcefiles</span>
          </span>
          <Button variant="primary" size="sm" onClick={handleAutoImport} disabled={autoImporting}>
            {autoImporting ? "Importing…" : "Import now"}
          </Button>
        </div>
      )}
      {autoStatus?.found && autoStatus.already_imported && (
        <div style={{
          fontSize: 12.5, color: "var(--success)", marginBottom: 14,
          display: "flex", alignItems: "center", gap: 6, overflowWrap: "anywhere",
        }}>
          <span aria-hidden="true">✓</span>
          <span>Today's report ({autoStatus.filename}) already imported</span>
        </div>
      )}

      <div
        className={`drop-zone ${dragging ? "dragging" : ""} ${file ? "selected-file" : ""}`}
        role="button" tabIndex={0}
        onClick={() => inputRef.current.click()}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); inputRef.current.click(); } }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={(e) => setFile(e.target.files[0])}
        />
        {file ? (
          <>
            <div className="drop-zone-icon"><FileIcon done /></div>
            <h4>{file.name}</h4>
            <p>{(file.size / 1024).toFixed(0)} KB — click to change</p>
          </>
        ) : (
          <>
            <div className="drop-zone-icon"><FileIcon /></div>
            <h4>Drop Excel file here or click to browse</h4>
            <p>Accepts .xlsx / .xls — must contain "Master Reorder" sheet</p>
          </>
        )}
      </div>

      <div className="form-row">
        <label>Reorder Request Date</label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>

      <button
        className="btn-upload"
        disabled={!file || !date || loading}
        onClick={handleSubmit}
      >
        {loading ? "Processing…" : "Upload & analyse"}
      </button>
      {loading && <div style={{ marginTop: 10 }}><LoadingBar /></div>}
    </div>
  );
}
